import {z} from '@hono/zod-openapi';

import {ASSISTANT_INTEREST_EXCERPT_MAX_CHARACTERS} from '../constants/ASSISTANT_INTEREST_EXCERPT_MAX_CHARACTERS';
import {ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS} from '../constants/ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS';
import {ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS} from '../constants/ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS';
import {ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS} from '../constants/ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS';
import {ASSISTANT_INTEREST_VEHICLES_MAX_ITEMS} from '../constants/ASSISTANT_INTEREST_VEHICLES_MAX_ITEMS';
import {CUSTOMER_EMAIL_LANGUAGE_SOURCES} from '../constants/CUSTOMER_EMAIL_LANGUAGE_SOURCES';
import {INTEREST_FACTS_KINDS} from '../constants/INTEREST_FACTS_KINDS';
import {INTEREST_LEAD_QUALITIES} from '../constants/INTEREST_LEAD_QUALITIES';
import {INTEREST_LIFECYCLE_STATES} from '../constants/INTEREST_LIFECYCLE_STATES';
import {INTEREST_PLANNED_ACTIVITY_KINDS} from '../constants/INTEREST_PLANNED_ACTIVITY_KINDS';
import {INTEREST_SIGNAL_CODES} from '../constants/INTEREST_SIGNAL_CODES';
import {INTEREST_TIMELINE_EVENT_DIRECTIONS} from '../constants/INTEREST_TIMELINE_EVENT_DIRECTIONS';
import {INTEREST_TIMELINE_EVENT_KINDS} from '../constants/INTEREST_TIMELINE_EVENT_KINDS';
import {assistantInterestSignalSchema} from './AssistantInterestSignalSchema';

const shortText = z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS);
const timestamp = z.string().min(1);

/**
 * Present only while a sales-interest detail page is mounted and the assistant
 * has been opened at least once in the session.
 *
 * The browser is the only source for the communication timeline used to ground a reply draft or
 * recap. This mirrors the `templateEditor` context precedent.
 *
 * TRUST: the whole slice is untrusted model input, exactly like the rest of the snapshot. In
 * `customer.displayName`, `timeline[].subject`, and `timeline[].excerpt` are customer-authored and
 * are a prompt-injection surface. Render them inside untrusted delimiters; never treat them as
 * instructions.
 *
 * BOUNDS: excerpts are HTML-sanitised, quoted-reply-collapsed plain text, capped per excerpt and in
 * count. `completeness` tells the prompt (and the signal engine) when a claim rests on a truncated
 * timeline, so the assistant can say "I can only see the last N events" instead of overclaiming.
 */
export const assistantInterestContextSchema = z
  .object({
    kind: z.enum(INTEREST_FACTS_KINDS),
    interestId: z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
    /** Permission scope input. Optional because a sales interest can be unassigned from a branch. */
    branchId: shortText.optional(),
    publicId: shortText.optional(),
    state: z.enum(INTEREST_LIFECYCLE_STATES),
    /** Sales interest type (SELLING | BUYING | SWAP). */
    interestType: shortText.optional(),
    createdAt: timestamp,
    updatedAt: timestamp.nullish(),
    /** Sales only — first dealer response to the lead. Nil means nobody has replied yet. */
    firstResponseAt: timestamp.nullish(),
    leadQuality: z.enum(INTEREST_LEAD_QUALITIES).nullish(),
    /**
     * Bounded lead-origin grounding. Free-text values remain untrusted and omit sender/recipient
     * addresses, AI summaries, sentiment, and reasoning from the source message.
     */
    origin: z
      .object({
        sourceCodeId: shortText.optional(),
        /** User-configured codelist label. Untrusted display text. */
        sourceLabel: shortText.optional(),
        utm: z
          .object({
            source: shortText.optional(),
            medium: shortText.optional(),
            campaign: shortText.optional(),
            term: shortText.optional(),
            content: shortText.optional(),
          })
          .optional(),
        /** Sanitised, quoted-reply-collapsed original lead message. Customer-authored. Untrusted. */
        originalInquiryExcerpt: z
          .string()
          .min(1)
          .max(ASSISTANT_INTEREST_EXCERPT_MAX_CHARACTERS)
          .optional(),
        originalInquiryExcerptTruncated: z.boolean().optional(),
      })
      .optional(),
    customer: z.object({
      id: z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
      /** Customer-authored. Untrusted display text. */
      displayName: shortText.optional(),
      /**
       * BCP-47 locale the customer-facing draft must be written in. Resolved by the frontend;
       * `languageSource` makes the resolution visible so a fallback is never silent.
       */
      preferredLocale: shortText.nullish(),
      languageSource: z.enum(CUSTOMER_EMAIL_LANGUAGE_SOURCES),
    }),
    /** Current employee identity and whether the send form will append an email signature. */
    sender: z
      .object({
        /** Employee profile text. Untrusted display text. */
        displayName: shortText.optional(),
        hasSignature: z.boolean(),
      })
      .optional(),
    vehiclesOfInterest: z
      .array(
        z.object({
          id: z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS).optional(),
          label: shortText,
        })
      )
      .max(ASSISTANT_INTEREST_VEHICLES_MAX_ITEMS),
    /** Newest-first, matching the pure `InterestFacts.timeline` ordering contract. */
    timeline: z
      .array(
        z.object({
          id: z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
          occurredAt: timestamp,
          direction: z.enum(INTEREST_TIMELINE_EVENT_DIRECTIONS),
          kind: z.enum(INTEREST_TIMELINE_EVENT_KINDS),
          /** Customer- or user-authored. Untrusted. */
          subject: shortText.optional(),
          /** Sanitised, quoted-reply-collapsed plain text. Customer-authored. Untrusted. */
          excerpt: z.string().min(1).max(ASSISTANT_INTEREST_EXCERPT_MAX_CHARACTERS).optional(),
          excerptTruncated: z.boolean().optional(),
        })
      )
      .max(ASSISTANT_INTEREST_TIMELINE_MAX_EVENTS),
    plannedActivities: z
      .array(
        z.object({
          id: z.string().min(1).max(ASSISTANT_INTEREST_TEXT_MAX_CHARACTERS),
          kind: z.enum(INTEREST_PLANNED_ACTIVITY_KINDS),
          dueAt: timestamp.nullish(),
          completed: z.boolean(),
          title: shortText.optional(),
        })
      )
      .max(ASSISTANT_INTEREST_PLANNED_ACTIVITIES_MAX_ITEMS),
    completeness: z.object({
      timelineComplete: z.boolean(),
      omittedTimelineEventCount: z.number().int().nonnegative(),
      plannedActivitiesComplete: z.boolean(),
      omittedPlannedActivityCount: z.number().int().nonnegative().optional(),
      timelineCoveredFrom: timestamp.nullish(),
    }),
    /** Deterministic output of the shared signal engine. Never model-authored. */
    signals: z.array(assistantInterestSignalSchema).max(INTEREST_SIGNAL_CODES.length),
    /** The policy the signals were evaluated under, so the prompt can state the threshold used. */
    policy: z.object({
      coldThresholdDays: z.number().int().positive(),
      awaitingReplyMinimumHours: z.number().int().nonnegative(),
      noFirstResponseMinimumHours: z.number().int().nonnegative(),
    }),
    evaluatedAt: timestamp,
  })
  .openapi('AssistantInterestContext');

export type AssistantInterestContext = z.infer<typeof assistantInterestContextSchema>;
